import { useGetMembersPlaces } from "@birdwatch/client";
import { useUser } from "@clerk/clerk-react";
import { useMemo, useState } from "react";

import { findPresaleResumes, type PresaleResume } from "./presale-resume";

/**
 * `-1` means "every place" (`FETCH_ALL_MAX_LIMIT`), and it MUST stay equal to
 * `MY_PLACES_LIST_TAKE` in `features/my/_hooks/use-my-places.ts`: the generated query key
 * includes the query object, so an equal `take` makes this and `/my/home` share one cache
 * entry. Change it and every member pays an extra `/members/places` round trip on the landing.
 */
const PLACES_TAKE = -1;

/**
 * The unfinished presale reservation this member should be sent back to, if any.
 *
 * One request, and one it does not pay for: `/members/places` is what `/my/home` and the landing
 * route fetch anyway, and the intent's own `payload.flow` says whether the presale funnel
 * created it — so nothing here needs to go read the campaign's `CheckoutLink` rows to find
 * out. That is why this can run on every post-auth landing.
 *
 * A failed lookup resolves to "no resume", not an error: nobody should be stuck on a
 * spinner because `/members/places` returned a 500.
 */
export function usePresaleResume(options?: { enabled?: boolean }): {
  resume: PresaleResume | null;
  /** True until the decision is final — including while Clerk is still loading. */
  isResolving: boolean;
} {
  const { isLoaded, isSignedIn } = useUser();
  const wanted = options?.enabled ?? true;
  const enabled = wanted && isLoaded && Boolean(isSignedIn);

  // Captured once: a `new Date()` in the render body is a fresh object every render, which
  // would defeat the memo below and let the launch-day gate flip mid-session.
  const [now] = useState(() => new Date());

  const placesQuery = useGetMembersPlaces()
    .query({ take: PLACES_TAKE })
    .get({ enabled });

  // Newest reservation first, so the head of the list is the one to reopen.
  const resume = useMemo(
    () => findPresaleResumes(placesQuery.data, now)[0] ?? null,
    [placesQuery.data, now],
  );

  const isFailed = placesQuery.isError;
  // `isLoading`, not `isPending`: a disabled query stays pending forever.
  const isResolving =
    wanted && !isFailed && (!isLoaded || placesQuery.isLoading);

  return { resume: isFailed ? null : resume, isResolving };
}
